import { track } from "../../clients/mixpanel";
import { FloorAreaItem } from "../../interfaces/AreaSheet";
import { ReportUseGroup } from "../../interfaces/EstimateUseGroup";
import { getEstimateReportData, setPrintAreaToUsedRange } from "../workbook";
import { ValidationError } from "../../errors";
import { ColumnNameToIndexMap, getReportOptions } from "./helpers/common";
import { CellOverride, RowFormat, createColumnNameToIndexMap, printFormattedData } from "./helpers/print";
import { handleDynamicReportGeneration, SheetDataItem } from "./helpers/dynamicSheets";
import { columnNames, rowNames } from "./helpers/rangeNames";
import { handleDynamicColumns } from "./helpers/print";
import { cacheableData } from "../workbook/cache";
import { fetchEstimateExport } from "../../api/estimates";
import { transformFloorAreas } from "../datasources/floorAreas";
import { transformUseGroups } from "../datasources/useGroups";
import { AREA_SHEET_REPORT_NAME } from "./api/reportNames";

export const areaSheetDefaultOptions = {
  includeFeatures: true,
} as AreaSheetReportOptions;

export interface AreaSheetReportOptions extends cacheableData {
  includeFeatures: boolean;
}

/**
 * The report card's view of the same data. An estimate that is not loaded is "no data" rather than a
 * failure — `fetchFloorAreasWithGroups` throws for it, which charts and tenant exports rely on, but a
 * throw would leave the card holding its previous status instead of showing its empty state.
 */
export const fetchFloorAreasForReport = async (): Promise<FloorAreaItem[]> => {
  const reportData = (await getEstimateReportData()) as any[];
  const estimateId = reportData[0]?.estimate_id as string | undefined;
  if (!estimateId) return [];
  return fetchFloorAreasWithGroups(estimateId);
};

export const fetchFloorAreasWithGroups = async (estimateIdParam?: string): Promise<FloorAreaItem[]> => {
  let estimateId = estimateIdParam;

  if (!estimateId) {
    const reportData = (await getEstimateReportData()) as any[];
    estimateId = reportData[0]?.estimate_id as string | undefined;
  }
  if (!estimateId) {
    throw new ValidationError("No estimate loaded. Please load an estimate first.");
  }

  const result = await fetchEstimateExport(estimateId);
  if (!result.success) {
    throw result.error;
  }
  if (!result.data) {
    throw new Error("Failed to fetch estimate data for area sheet");
  }

  const { area_matrix_items, area_matrix_item_estimate_use_groups, estimate, floor_groups } = result.data;
  const estimate_use_groups = estimate.stats.useGroups || [];
  return transformFloorAreas(
    area_matrix_items,
    area_matrix_item_estimate_use_groups,
    estimate_use_groups,
    floor_groups
  );
};

export const generateAreaSheetReports = async (context: Excel.RequestContext, templateWorksheet: Excel.Worksheet) => {
  const startTime = performance.now();
  const options = (await getReportOptions(templateWorksheet, areaSheetDefaultOptions)) as AreaSheetReportOptions;

  const reportData = (await getEstimateReportData()) as any[];
  const estimateId = reportData[0]?.estimate_id as string | undefined;
  if (!estimateId) {
    throw new ValidationError("No estimate loaded. Please load an estimate first.");
  }

  const estimateResult = await fetchEstimateExport(estimateId);
  if (!estimateResult.success || !estimateResult.data) {
    throw new ValidationError("Failed to fetch estimate data for Area Sheet report.");
  }

  const { area_matrix_items, area_matrix_item_estimate_use_groups, estimate, floor_groups } = estimateResult.data;
  const estimate_use_groups = estimate.stats.useGroups || [];
  const allFloorAreas = transformFloorAreas(
    area_matrix_items,
    area_matrix_item_estimate_use_groups,
    estimate_use_groups,
    floor_groups
  );
  const useGroups = transformUseGroups(estimate_use_groups, floor_groups);
  useGroups.sort((a, b) => a.order - b.order);

  const data = allFloorAreas;

  if (data.length === 0) {
    throw new ValidationError("Load an estimate before generating a report.");
  }

  const floorAreaData = options.includeFeatures
    ? data
    : data.filter((item) => item.type.toLowerCase().trim() !== "feature");

  // Dynamically generate sheets for each unique floor group in the data and return their associated data
  const sheets = await handleDynamicReportGeneration(
    context,
    templateWorksheet,
    AREA_SHEET_REPORT_NAME,
    floorAreaData,
    "floor_group"
  );

  // For each sheet, call generateAreaSheetReport
  for (let i = 0; i < sheets.length; i++) {
    const sheetItem = sheets[i];
    await generateAreaSheetReport(useGroups, sheetItem);
  }

  track("excel.createReport.areaSheet", {
    loadTime: (performance.now() - startTime) / 1000,
  });
};

export const generateAreaSheetReport = async (useGroups: ReportUseGroup[], sheetData: SheetDataItem) => {
  // Each sheet generation should be its own Excel.run to avoid context issues
  await Excel.run(async (context) => {
    const worksheet = context.workbook.worksheets.getItem(sheetData.sheetName);

    let buildingUseGroups = useGroups || [];
    if (sheetData.key !== "unassigned") {
      const filteredUseGroups =
        useGroups.filter((ug) => {
          // NOTE: Will this cause issues with the edge case that we're not handling is line items allocated to multiple use groups with the same label (DO-135)
          const assignedUseGroups = ug.floor_group === sheetData.key;
          const hasAreaKey = sheetData.data.some(
            (item) =>
              Object.keys(item).includes(`${ug.name}_area`) &&
              item[`${ug.name}_area`] !== undefined &&
              item[`${ug.name}_area`] !== 0
          );
          if (assignedUseGroups || hasAreaKey) {
            return true;
          }
          return false;
        }) || [];
      buildingUseGroups = filteredUseGroups;
    }

    await handleDynamicColumns(worksheet, columnNames.useGroups, buildingUseGroups);

    const columnMap = await createColumnNameToIndexMap(worksheet);
    const { mappedArray, formatList } = areaSheetArray(sheetData.data, columnMap, buildingUseGroups);

    await printFormattedData({
      dataArray: mappedArray,
      formatList,
      worksheet,
      insertAtRangeName: rowNames.floorAreaItems,
      startColumnIndex: 1,
      options: {
        groupRows: true,
      },
    });

    await setPrintAreaToUsedRange(worksheet);
  });
};

const areaSheetArray = (
  floorAreaData: FloorAreaItem[],
  columnMap: ColumnNameToIndexMap,
  useGroups: ReportUseGroup[]
) => {
  // Dynamically calculate the column indices for use groups
  let firstUseGroupColumnIndex = columnMap.use_groups;
  const useGroupColumnIndices: Record<string, number> = {};

  useGroups.forEach((useGroup: ReportUseGroup) => {
    // Store the column index for this use group
    const columnIndex = firstUseGroupColumnIndex++;
    useGroupColumnIndices[useGroup.id] = columnIndex;

    // Add mapping for dynamic use group columns to columnMap
    columnMap[`${useGroup.name}_area`] = columnIndex;
  });

  // Group data by floor types
  const orderedData = {
    ABOVE_GRADE: [] as FloorAreaItem[],
    BELOW_GRADE: [] as FloorAreaItem[],
    FEATURE: [] as FloorAreaItem[],
  };

  const groupedData = floorAreaData.reduce(
    (acc, item) => {
      if (!acc[item.type]) {
        acc[item.type] = [];
      }
      acc[item.type].push(item);
      return acc;
    },
    orderedData as Record<string, FloorAreaItem[]>
  );

  // Calculate the total number of columns needed based on the maximum column index
  const maxColumnIndex = Math.max(...Object.values(columnMap).filter((v): v is number => typeof v === "number"));
  const totalColumns = maxColumnIndex + 1;

  // Create format list and mapped array for data display
  const formatList: RowFormat[] = [];
  const mappedArray: (string | number)[][] = Object.entries(groupedData)
    .filter(([_, items]) => items.length > 0) // Only include non-empty categories
    .flatMap(([type, items]) => {
      // Create a row for the group type header
      const typeRow: (string | number)[] = new Array(totalColumns).fill("");
      typeRow[0] = type
        .toLowerCase()
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

      // if BELOW_GRADE sort by floor_number descending, otherwise sort by floor_number ascending
      if (type === "ABOVE_GRADE") {
        items.sort((a, b) => (b.floor_number || 0) - (a.floor_number || 0));
      } else {
        items.sort((a, b) => (a.floor_number || 0) - (b.floor_number || 0));
      }

      // Create rows for each item in the group
      const itemRows = items.map((item, i) => {
        const row: (string | number)[] = new Array(totalColumns).fill("");

        Object.keys(item).forEach((key) => {
          if (columnMap[key] !== undefined) {
            if (key === "floor_number") {
              row[columnMap[key]] = type === "ABOVE_GRADE" ? items.length - i : i + 1;
            } else {
              const value = item[key as keyof FloorAreaItem];
              row[columnMap[key]] = value !== undefined ? value : "";
            }
          }
        });

        return row;
      });

      // Define cell overrides for formatting
      const cellOverrides = [
        { columnIndex: 3, style: "spacerOuterBorders" }, // 1st spacer
        { columnIndex: totalColumns, style: "spacerOuterBorderLeft" }, // end
      ] as CellOverride[];

      // Add header style to the type row
      formatList.push({
        style: "header",
        level: 0,
        indentColumn: undefined,
        group: [],
        cellOverrides,
      });

      // Add item style to all item rows
      formatList.push(
        ...new Array(itemRows.length).fill({
          style: "gridItem",
          level: 0,
          indentColumn: undefined,
          group: [1],
          cellOverrides,
        })
      );

      return [typeRow, ...itemRows];
    });

  return { mappedArray, formatList };
};
